/**
 * Canonical AI add-on pricing catalog — must match Osync-AWS
 * {@code AddonUsageConstants.AiAddonPricingFeature} and migration seed rows
 * ({@code AddonUsageTableMigration}).
 *
 * Use these wherever the UI references a pricing feature display name, flag, key,
 * or AppContext property key so renames stay consistent across repos.
 */
const AI_ADDON_FEATURE = Object.freeze([
  {
    id: "REPHRASE",
    featureName: "Rephrase",
    featureFlag: 100,
    featureKey: "AI_Messaging",
    appPropertyKey: "isRephraseEnabled",
    description: "Rewrite outbound messages before you send.",
  },
  {
    id: "AUTO_REPLY",
    featureName: "Auto Reply",
    featureFlag: 101,
    featureKey: "AI_Messaging",
    appPropertyKey: "isAutoReplyEnabled",
    description: "Automatically respond to inbound messages based on your rules.",
  },
  {
    id: "CALL_SUMMARY",
    featureName: "Call Summary",
    featureFlag: 102,
    featureKey: "AI_Call",
    appPropertyKey: "isCallSummaryEnabled",
    description: "Summarize completed calls for faster follow-up.",
  },
  {
    id: "CALL_INTERACTION",
    featureName: "Call Interaction",
    featureFlag: 103,
    featureKey: "AI_Call",
    appPropertyKey: null,
    description: "AI-assisted context and actions for live or completed calls.",
  },
  {
    id: "MESSAGE_REPLY_SUGGESTION",
    featureName: "Message Reply Suggestion",
    featureFlag: 104,
    featureKey: "AI_Messaging",
    appPropertyKey: "isAutoSuggestionEnabled",
    description: "Suggested replies while you compose messages.",
  },
]);

const getFeatureById = Object.freeze(
  Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.id, entry]))
);

const getFeatureByFeatureName = Object.freeze(
  Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.featureName, entry]))
);

const getFeatureByFeatureFlag = Object.freeze(
  Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.featureFlag, entry]))
);

/** {@code PricingFeaturesEntity.featureName} values for the AI add-on group. */
export const AI_ADDON_FEATURE_NAMES = Object.freeze(
  Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.id, entry.featureName]))
);

/** {@code AiAddonPricingFeature.featureFlag} values. */
export const AI_ADDON_FEATURE_FLAGS = Object.freeze(
  Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.id, entry.featureFlag]))
);

export const AI_ADDON_FEATURE_KEYS = Object.freeze({
  AI_MESSAGING: "AI_Messaging",
  AI_CALL: "AI_Call",
});

/**
 * AppContext (`appProperties`) keys for AI feature enabled flags, keyed by catalog id.
 * Populated from IntegrationResponse on load and updated after Settings → AI Features toggles.
 * MESSAGE_REPLY_SUGGESTION maps to `isAutoSuggestionEnabled` (legacy API field name).
 */
export const AI_APP_PROPERTY_KEYS = Object.freeze(
  Object.fromEntries(
    AI_ADDON_FEATURE.filter((entry) => entry.appPropertyKey).map((entry) => [
      entry.id,
      entry.appPropertyKey,
    ])
  )
);

export const AI_FEATURE_DESCRIPTIONS_BY_FEATURE_NAME = Object.freeze(
  Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.featureName, entry.description]))
);

/**
 * @param {string} id - e.g. `REPHRASE`
 * @returns {typeof AI_ADDON_FEATURE[number]|undefined}
 */
export const getAiAddonFeatureById = (id) => getFeatureById[id];

/**
 * @param {number|string} featureFlag
 * @returns {typeof AI_ADDON_FEATURE[number]|undefined}
 */
export const getAiAddonFeatureByFlag = (featureFlag) => {
  const normalizedFeatureFlag = typeof featureFlag === "number" ? featureFlag : Number(featureFlag);
  if (!Number.isFinite(normalizedFeatureFlag)) {
    return undefined;
  }
  return getFeatureByFeatureFlag[normalizedFeatureFlag];
};

/**
 * @param {string} featureName - `PricingFeaturesEntity.featureName`
 * @returns {typeof AI_ADDON_FEATURE[number]|undefined}
 */
export const getAiAddonFeatureByName = (featureName) => {
  if (typeof featureName !== "string") {
    return undefined;
  }
  return getFeatureByFeatureName[featureName];
};

/**
 * @param {string} [featureName] - `PricingFeaturesEntity.featureName`
 * @param {string} [descriptionFromApi] - optional when backend adds a description attribute
 * @returns {string}
 */
export const getAIFeatureDescriptionForListing = (featureName, descriptionFromApi) => {
  if (typeof descriptionFromApi === "string" && descriptionFromApi.trim() !== "") {
    return descriptionFromApi.trim();
  }
  if (!featureName || typeof featureName !== "string") {
    return "";
  }
  return AI_FEATURE_DESCRIPTIONS_BY_FEATURE_NAME[featureName] ?? "";
};
